// src/components/navbar/Navbar.js

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import "./Navbar.css";

function Navbar({
  theme,
  toggleTheme,
  sidebarOpen,
  toggleSidebar,
  function_name,
  userLogin,
  userRole = "viewer",
  weekList = [],
  yearList = [],
  defaultWeek = 1,
  defaultYear = 2026,
  onWeekChange,
  onYearChange,
  onLogout,
  onAdminClick,
  onUploadClick,
  isAdminActive = false,
  isUploadActive = false,
  showAdminButton = false,
  showUploadButton = false,
  showBackButton = true,
}) {
  const [activeDropdown, setActiveDropdown] = useState(null);
  const navigate = useNavigate();

  const selectedWeek = defaultWeek;
  const selectedYear = defaultYear;

  const handleDropdownToggle = (dropdown) => {
    setActiveDropdown(activeDropdown === dropdown ? null : dropdown);
  };

  const handleWeekSelect = (weekNum) => {
    setActiveDropdown(null);
    onWeekChange && onWeekChange(weekNum);
  };

  const handleYearSelect = (yearNum) => {
    setActiveDropdown(null);
    onYearChange && onYearChange(yearNum);
  };

  const getInitials = (name) => {
    if (!name || name === "Guest") return "?";
    return name
      .split(" ")
      .map((part) => part[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const getRoleBadgeClass = () => {
    switch (userRole) {
      case "admin":
        return "role-badge--admin";
      case "editor":
        return "role-badge--editor";
      case "viewer":
        return "role-badge--viewer";
      default:
        return "role-badge--viewer";
    }
  };

  return (
    <>
      {activeDropdown && (
        <div
          className="navbar-backdrop"
          onClick={() => setActiveDropdown(null)}
        />
      )}

      <nav className="navbar">
        <div className="navbar-inner">
          {/* ─── Left ─── */}
          <div className="navbar-left">
            {toggleSidebar && (
              <button
                className="sidebar-toggle"
                onClick={toggleSidebar}
                aria-label="Toggle sidebar"
              >
                {sidebarOpen ? "✕" : "☰"}
              </button>
            )}
            {showBackButton && (
              <button
                className="navbar-back-btn"
                onClick={() => navigate("/")}
                aria-label="Back to Pulse Suite"
                title="Back to Pulse Suite"
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  width="16"
                  height="16"
                >
                  <line x1="19" y1="12" x2="5" y2="12" />
                  <polyline points="12 19 5 12 12 5" />
                </svg>
                <span className="navbar-back-label">Suite</span>
              </button>
            )}
            <div className="navbar-brand">
              <span className="brand-mark">{function_name}</span>
            </div>
          </div>

          {/* ─── Center ─── */}
          <div className="navbar-center">
            {weekList.length > 0 && (
              <div className="dropdown-wrapper">
                <button
                  className={`dropdown-trigger ${
                    activeDropdown === "week" ? "active" : ""
                  }`}
                  onClick={() => handleDropdownToggle("week")}
                >
                  <svg
                    className="dropdown-icon"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                    <line x1="16" y1="2" x2="16" y2="6" />
                    <line x1="8" y1="2" x2="8" y2="6" />
                    <line x1="3" y1="10" x2="21" y2="10" />
                  </svg>
                  <span className="dropdown-label">Week {selectedWeek}</span>
                  <svg
                    className="dropdown-chevron"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </button>

                {activeDropdown === "week" && (
                  <div className="dropdown-menu dropdown-menu-weeks">
                    <div className="dropdown-menu-title">Select Week</div>
                    <div className="dropdown-list-scroll">
                      {weekList.map((wk) => (
                        <button
                          key={wk}
                          className={`dropdown-item ${
                            selectedWeek === wk ? "selected" : ""
                          }`}
                          onClick={() => handleWeekSelect(wk)}
                        >
                          {selectedWeek === wk && (
                            <span className="check-icon">✓</span>
                          )}
                          Week {wk}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {yearList.length > 0 && (
              <div className="dropdown-wrapper">
                <button
                  className={`dropdown-trigger ${
                    activeDropdown === "year" ? "active" : ""
                  }`}
                  onClick={() => handleDropdownToggle("year")}
                >
                  <svg
                    className="dropdown-icon"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <polyline points="12 6 12 12 16 14" />
                  </svg>
                  <span className="dropdown-label">{selectedYear}</span>
                  <svg
                    className="dropdown-chevron"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </button>

                {activeDropdown === "year" && (
                  <div className="dropdown-menu">
                    <div className="dropdown-menu-title">Select Year</div>
                    {yearList.map((yr) => (
                      <button
                        key={yr}
                        className={`dropdown-item ${
                          selectedYear === yr ? "selected" : ""
                        }`}
                        onClick={() => handleYearSelect(yr)}
                      >
                        {selectedYear === yr && (
                          <span className="check-icon">✓</span>
                        )}
                        {yr}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ─── Right ─── */}
          <div className="navbar-right">
            {/* ─── Upload Data Button (standalone) ─── */}
            {showUploadButton && (
              <button
                className={`navbar-upload-btn ${
                  isUploadActive ? "navbar-upload-btn--active" : ""
                }`}
                onClick={() => {
                  if (onUploadClick) {
                    onUploadClick();
                  } else {
                    navigate("/upload");
                  }
                }}
                aria-label="Upload Data"
              >
                <svg
                  className="navbar-upload-icon"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
                <span className="navbar-upload-label">Upload</span>
              </button>
            )}

            <button className="theme-toggle" onClick={toggleTheme}>
              <span className="theme-toggle-icon">
                {theme === "light" ? "🌙" : "☀️"}
              </span>
            </button>

            <div className="dropdown-wrapper">
              <button
                className={`user-action-trigger ${
                  activeDropdown === "actions" ? "active" : ""
                }`}
                onClick={() => handleDropdownToggle("actions")}
                aria-label="User menu"
              >
                <span className="user-avatar">{getInitials(userLogin)}</span>
                <span className="user-name-label">{userLogin}</span>
                <span className={`role-indicator ${getRoleBadgeClass()}`} />
                <svg
                  className="action-menu-icon"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <circle cx="12" cy="5" r="1.5" />
                  <circle cx="12" cy="12" r="1.5" />
                  <circle cx="12" cy="19" r="1.5" />
                </svg>
              </button>

              {activeDropdown === "actions" && (
                <div className="dropdown-menu dropdown-menu-actions">

                  
                  {/* ─── User Header with Role ─── */}
                  <div className="dropdown-user-header">
                    <span className="dropdown-user-avatar">
                      {getInitials(userLogin)}
                    </span>
                    <div className="dropdown-user-info">
                      <span className="dropdown-user-name">{userLogin}</span>
                      <span
                        className={`dropdown-user-role ${getRoleBadgeClass()}`}
                      >
                        {userRole.charAt(0).toUpperCase() + userRole.slice(1)}
                      </span>
                    </div>
                  </div>

                  <div className="dropdown-divider"></div>

                  {/* ─── Refresh ─── */}
                  <button
                    className="dropdown-item"
                    onClick={() => {
                      setActiveDropdown(null);
                      window.location.reload();
                    }}
                  >
                    <svg
                      className="action-item-icon"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <polyline points="23 4 23 10 17 10" />
                      <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
                    </svg>
                    Refresh
                  </button>

                  {/* ─── Admin Panel (admin only) ─── */}
                  {showAdminButton && (
                    <button
                      className={`dropdown-item ${
                        isAdminActive ? "dropdown-item--active" : ""
                      }`}
                      onClick={() => {
                        setActiveDropdown(null);
                        if (onAdminClick) {
                          onAdminClick();
                        } else {
                          navigate("/admin");
                        }
                      }}
                    >
                      <svg
                        className="action-item-icon"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <circle cx="12" cy="12" r="3" />
                        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
                      </svg>
                      Admin Panel
                      {isAdminActive && (
                        <span className="dropdown-item-active-dot"></span>
                      )}
                    </button>
                  )}

                  <div className="dropdown-divider"></div>

                  {/* ─── Logout ─── */}
                  {onLogout && (
                    <button
                      className="dropdown-item dropdown-item--danger"
                      onClick={() => {
                        setActiveDropdown(null);
                        onLogout();
                      }}
                    >
                      <svg
                        className="action-item-icon"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                        <polyline points="16 17 21 12 16 7" />
                        <line x1="21" y1="12" x2="9" y2="12" />
                      </svg>
                      Logout
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>
    </>
  );
}

export default Navbar;